import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaUsers,
  FaChartLine,
  FaDatabase,
  FaHeartbeat,
  FaSync,
  FaProjectDiagram,
  FaUserCheck,
  FaCloud,
  FaServer,
  FaExpand,
} from "react-icons/fa";
import Chart from "react-apexcharts";
import {
  fetchDashboardKpis,
  getAvailablePlans,
} from "../services/analyticsService";
import TimeFilter from "../components/TimeFilter";
import LoadingOverlay from "../components/LoadingOverlay";
import ExpandedChartModal from "../components/ExpandedChartModal";
import InfoTooltip from "../components/InfoTooltip";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTimeFilter } from "../context/TimeFilterContext";
import { getNodeTitle } from "../utils/nodeMappings";

/**
 * @file Dashboard.js
 * @description Main Super Admin Dashboard. Displays global KPIs, execution trends, and system health.
 */

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedChart, setExpandedChart] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const handleExpandChart = (title, options, series, type) => {
    setExpandedChart({ title, options, series, type });
  };

  // Global Time Filter State
  const { timeFilter, setTimeFilter, customRange, handleCustomRangeChange,
    planFilter, setPlanFilter,
    availablePlans, setAvailablePlans
  } =
    useTimeFilter();


  useEffect(() => {
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
    getAvailablePlans(timeFilter, customRange).then(setAvailablePlans);
  }, [timeFilter, customRange]);

  /**
   * Fetches all dashboard data based on current active filters.
   * Updates state and last updated timestamp.
   */
  const loadData = useCallback(
    async (forceRefresh = false) => {
      // Prevent API call if custom range is incomplete
      if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) {
        return;
      }
      if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
      setLoading(true);
      // Passing the global filters to the API, and forceRefresh
      const res = await fetchDashboardKpis(
        timeFilter,
        customRange,
        planFilter,
        forceRefresh,
      );

      // Also refresh plans if forceRefresh is true
      if (forceRefresh) {
        getAvailablePlans(timeFilter, customRange, true).then(setAvailablePlans);
      }

      if (res) {
        setData(res);
        setLastUpdated(new Date());
      }

      // Add a small delay to allow charts to render/paint behind the loader
      // This prevents the "pop-in" effect and makes the transition smoother
      setTimeout(() => {
        setLoading(false);
      }, 600);
    },
    [timeFilter, customRange, planFilter],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading && !data) {
    return (
      <div className="p-4 bg-light min-vh-100 position-relative">
        <LoadingOverlay loading={true} message="Initializing Dashboard..." />
      </div>
    );
  }

  if (!data)
    return (
      <div className="p-5 text-center text-danger">
        Error: Could not retrieve platform data.
      </div>
    );

  // --- DATA EXTRACTION FROM API ---
  const {
    platform_summary: summary,
    system_health: health,
    storage_metrics: storage = {},
    module_usage_distribution: modules,
    execution_history: history,
  } = data;

  // 1. KPI Cards Mapping
  const kpis = [
    {
      title: "Total Admins",
      value: summary.total_admins,
      change: "Platform",
      icon: FaBuilding,
      color: "#AD2D45",
      path: "/tenants",
      tip: "Total number of administrators (plan purchasers).",
    },
    {
      title: "Total Users",
      value: summary.total_users,
      change: "All Time",
      icon: FaUsers,
      color: "#912059",
      path: "/analytics",
      tip: "Total registered users across all tenants.",
    },
    {
      title: "Active Users",
      value: summary.active_users_count,
      change: `${summary.active_user_percentage}%`,
      icon: FaUserCheck,
      color: "#E68F17",
      path: "/analytics",
      tip: "Users active within the selected time period.",
    },
    {
      title: "Total Projects",
      value: summary.total_projects,
      change: "All Time",
      icon: FaProjectDiagram,
      color: "#F4D677",
      path: "/analytics",
      tip: "Total number of projects created.",
    },
    {
      title: "Executions",
      value: summary.total_executions,
      change: summary.time_period,
      icon: FaChartLine,
      color: "#FBC70F",
      path: "/analytics",
      tip: "Total workflow executions processed.",
    },
    {
      title: "System Health",
      value: health.health_status,
      icon: FaHeartbeat,
      color: health.health_status === "Excellent" ? "#10b981" : "#AD2D45",
      path: "/health",
      tip: "Overall system health status based on node uptime.",
    },
  ];

  // 2. Chart: Execution Trend (Using NEW execution_history Key)
  const trendSeries = [
    {
      name: "Executions",
      data: history?.map((h) => Number(h.count) || 0) || [],
    },
  ];

  const trendLabels =
    history?.map((h) => {
      const d = new Date(h.date);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }) || [];

  // 3. Chart: Plan Distribution
  const planEntries = Object.entries(summary.plan_distribution || {}).filter(
    ([_, v]) => v > 0,
  );
  const planSeries = planEntries.map((e) => Number(e[1]) || 0);
  const planLabels = planEntries.map((e) => e[0].toUpperCase());

  // 4. Chart: Module Usage
  const rawModLabels = Object.keys(modules || {});
  const modSeries = rawModLabels.map((k) => Number(modules[k].count) || 0);
  const modLabels = rawModLabels.map(getNodeTitle);

  const planOptions = {
    chart: { type: "donut", animations: { enabled: false } }, // Disable animations
    labels: planLabels,
    colors: ["#AD2D45", "#912059", "#E68F17", "#F4D677", "#FBC70F"],
    legend: { position: "bottom", fontSize: "12px" },
    plotOptions: {
      pie: {
        donut: {
          size: "75%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Admins",
              formatter: () => summary.total_admins,
            },
          },
        },
      },
    },
  };

  const moduleOptions = {
    chart: { toolbar: { show: false }, animations: { enabled: false } }, // Disable animations
    plotOptions: {
      bar: { horizontal: true, borderRadius: 5, barHeight: "40%" },
    },
    colors: ["#912059"],
    xaxis: { categories: modLabels },
    grid: { show: false },
  };

  return (
    <div className="p-4 bg-light min-vh-100 position-relative">
      <LoadingOverlay loading={loading} />
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark mb-0">Super Admin Dashboard</h3>
          <p className="text-muted small mb-0">
            System performance: <strong>{summary.time_period}</strong>
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm shadow-sm border-secondary-subtle"
            style={{ width: "130px", fontWeight: "500" }}

            value={planFilter || ""}
            onChange={(e) =>
              setPlanFilter(e.target.value === "" ? null : e.target.value)
            }
          >
            <option value="">All Plans</option>
            {availablePlans.map((plan) => (
              <option key={plan} value={plan}>
                {plan.charAt(0).toUpperCase() + plan.slice(1)}
              </option>
            ))}
          </select>
          <TimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            startDate={customRange.start}
            endDate={customRange.end}
            onCustomRangeChange={handleCustomRangeChange}
          />
          <div className="text-end d-none d-md-block">
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              Last Update
            </div>
            <div className="fw-bold small">
              {lastUpdated instanceof Date
                ? lastUpdated.toLocaleTimeString()
                : "-"}
            </div>
          </div>
          <button
            className="btn btn-white border shadow-sm btn-sm px-3"
            onClick={() => loadData(true)}
            disabled={loading}
          >
            <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI GRID */}
      <div className="row g-3 mb-4">
        {kpis.map((kpi, i) => (
          <div key={i} className="col-xl-2 col-md-4 col-6">
            <div
              className="card border-0 shadow-sm h-100 kpi-card-hover"
              onClick={() => kpi.path !== "#" && navigate(kpi.path)}
              style={{ cursor: "pointer" }}
            >
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div
                    className="p-2 rounded-2"
                    style={{
                      backgroundColor: `${kpi.color}15`,
                      color: kpi.color,
                    }}
                  >
                    <kpi.icon size={18} />
                  </div>
                  <span
                    className="badge bg-light text-primary border-0 small"
                    style={{ fontSize: "0.6rem" }}
                  >
                    {kpi.change}
                  </span>
                </div>
                <h4 className="fw-bold mb-0">{kpi.value}</h4>
                <div
                  className="text-muted small fw-semibold text-uppercase mt-1"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}
                >
                  {kpi.title} {kpi.tip && <InfoTooltip text={kpi.tip} />}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CHARTS ROW 1 */}
      <div className="row g-4 mb-4">
        {/* Execution Trend Area Chart */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold mb-0">
                  Execution Performance{" "}
                  <InfoTooltip text="Visualizes the total number of executions processed over time." />
                </h6>
                <small className="text-muted">
                  Total processed workflows over time
                </small>
              </div>
              <button
                className="btn btn-sm btn-link text-secondary p-0"
                onClick={() =>
                  handleExpandChart(
                    "Execution Performance",
                    {
                      chart: {
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        animations: { enabled: false },
                      },
                      stroke: { curve: "smooth", width: 3 },
                      colors: ["#AD2D45"],
                      xaxis: {
                        categories: trendLabels,
                        axisBorder: { show: false },
                      },
                      yaxis: { labels: { formatter: (val) => val.toFixed(0) } },
                      fill: {
                        type: "gradient",
                        gradient: {
                          shadeIntensity: 1,
                          opacityFrom: 0.4,
                          opacityTo: 0.1,
                        },
                      },
                      markers: { size: 4, colors: ["#AD2D45"], strokeWidth: 2 },
                    },
                    trendSeries,
                    "area",
                  )
                }
              >
                <FaExpand />
              </button>
            </div>
            {history && history.length > 0 ? (
              <Chart
                options={{
                  chart: {
                    toolbar: { show: false },
                    zoom: { enabled: false },
                    animations: { enabled: false },
                  },
                  stroke: { curve: "smooth", width: 3 },
                  colors: ["#AD2D45"],
                  xaxis: {
                    categories: trendLabels,
                    axisBorder: { show: false },
                  },
                  yaxis: { labels: { formatter: (val) => val.toFixed(0) } },
                  fill: {
                    type: "gradient",
                    gradient: {
                      shadeIntensity: 1,
                      opacityFrom: 0.4,
                      opacityTo: 0.1,
                    },
                  },
                  markers: { size: 4, hover: { size: 6 } }, // Ensure single points are visible
                }}
                series={trendSeries}
                type="area"
                height={300}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "300px" }}>
                No history data found for this period
              </div>
            )}
          </div>
        </div>

        {/* Plan Distribution Donut */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="fw-bold mb-0 text-uppercase">
                Subscription Distribution{" "}
                <InfoTooltip text="Breakdown of subscriptions by plan type." />
              </h6>
              <button
                className="btn btn-sm btn-link text-secondary p-0"
                onClick={() =>
                  handleExpandChart(
                    "Subscription Distribution",
                    planOptions,
                    planSeries,
                    "donut",
                  )
                }
              >
                <FaExpand />
              </button>
            </div>
            {planSeries.some(v => v > 0) ? (
              <Chart
                options={planOptions}
                series={planSeries}
                type="donut"
                height={320}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "320px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CHARTS ROW 2 */}
      <div className="row g-4">
        {/* Module Adoption Bar Chart */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">
                Assets Created{" "}
                <InfoTooltip text="Number of existing workflows, projects, and agents" />
              </h6>
              <button
                className="btn btn-sm btn-link text-secondary p-0"
                onClick={() =>
                  handleExpandChart(
                    "Module Adoption",
                    moduleOptions,
                    [{ name: "Usage Count", data: modSeries }],
                    "bar",
                  )
                }
              >
                <FaExpand />
              </button>
            </div>
            {modSeries.some(v => v > 0) ? (
              <Chart
                options={moduleOptions}
                series={[{ name: "Usage Count", data: modSeries }]}
                type="bar"
                height={200}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "200px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>

        {/* Infrastructure Storage Progress */}
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100 d-flex flex-row">
            <div className="flex-grow-1">
              <h6 className="fw-bold mb-1 text-uppercase">
                Infrastructure Storage{" "}
                <InfoTooltip text="Total vector and file storage usage vs limits." />
              </h6>
              <p className="text-muted small mb-3">
                Usage vs. Global Platform Limit (
                {(
                  (storage?.milvus_limit_per_org_gb || 0) +
                  (storage?.mysql_limit_per_org_gb || 0)
                ).toLocaleString()}{" "}
                GB per Org)
              </p>
              <div className="d-flex align-items-baseline gap-2">
                <h2 className="fw-bold mb-0">
                  {(
                    (storage?.total_milvus_mb || 0) +
                    (storage?.total_mysql_mb || 0)
                  ).toFixed(2)}
                </h2>
                <span className="text-muted fw-bold">MB</span>
              </div>
              <div
                className="progress mt-3"
                style={{ height: "10px", borderRadius: "10px" }}
              >
                <div
                  className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                  style={{
                    width: "10%", // detailed percentage calculation requires more derived data
                  }}
                ></div>
              </div>
              <div className="text-muted mt-2 small">
                Milvus: {(storage?.total_milvus_mb || 0).toFixed(2)} MB | MySQL:{" "}
                {(storage?.total_mysql_mb || 0).toFixed(2)} MB
              </div>
            </div>
            <div
              className="ms-4 p-4 bg-primary-subtle rounded-circle text-primary d-none d-sm-block align-self-center"
              style={{
                position: "relative",
                top: "-15%",
                left: "10%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <FaDatabase size={40} />
            </div>
          </div>
        </div>
      </div>
      <ExpandedChartModal
        show={!!expandedChart}
        onClose={() => setExpandedChart(null)}
        {...expandedChart}
      />
    </div>
  );
};

export default Dashboard;
